<template>
  <article class="bank-card" :class="{ 'is-blacklisted': isBlacklisted }" @click="editUser">
    <!-- ==================== Top: identity + pills ==================== -->
    <div class="card-top">
      <div class="avatar" :title="user.username">{{ initialsOf(user) }}</div>
      <div class="bank-meta">
        <div class="bank-name">{{ user.username }}</div>
        <div class="bank-sub">
          <span v-if="user.upiId" class="upi-tag" title="UPI ID">{{ user.upiId }}</span>
          <template v-if="!viewerIsSubAdmin">
            <span v-if="user.branch_id" class="dot-sep">·</span>
            <span v-if="user.branch_id" class="branch-tag">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M3 10l9-6 9 6M4 10v9M20 10v9M8 10v9M12 10v9M16 10v9M2 21h20" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ branchLabel }}
            </span>
            <span v-else-if="!user.branch_id" class="dot-sep">·</span>
            <span v-if="!user.branch_id" class="branch-none">No branch</span>
          </template>
        </div>
      </div>
      <div class="card-badges" @click.stop>
        <span
          v-if="isBlacklisted"
          class="pill pill-blacklist"
          :title="viewerIsVendor
            ? 'Vendor blacklisted — status is forced Inactive until lifted.'
            : 'This bank is blacklisted. Please contact support.'"
        >Blacklisted</span>
        <span
          class="pill"
          :class="user.status ? 'pill-success' : 'pill-neutral'"
        >{{ user.status ? 'Active' : 'Inactive' }}</span>
        <span
          class="pill"
          :class="user.isLoggedIn ? 'pill-info' : 'pill-neutral'"
        >{{ user.isLoggedIn ? 'Logged in' : 'Logged out' }}</span>
      </div>
    </div>

    <!-- ==================== Stats row ==================== -->
    <div class="card-stats">
      <div class="stat-cell">
        <div class="stat-label">Pay-in approved</div>
        <div class="stat-value">{{ fmtMoney(getVal('payInApprovedAmount', 'approvedAmount')) }}</div>
        <div class="stat-sub">{{ getVal('payInApprovedCount', 'approvedCount') || 0 }} orders</div>
      </div>
      <div class="stat-cell">
        <div class="stat-label">Pay-in limit left</div>
        <div class="stat-value">{{ fmtMoney(getVal('payInRemainingLimit', 'payInLimit')) }}</div>
        <div class="stat-sub">today's remaining</div>
      </div>
      <div class="stat-cell">
        <div class="stat-label">Daily pay-in limit</div>
        <div class="stat-value">
          <span v-if="hasDailyLimit">{{ fmtMoney(user.per_day_payin_limit) }}</span>
          <span v-else class="stat-empty">Not set</span>
        </div>
        <div class="stat-sub">resets at 12:00 AM IST</div>
      </div>
    </div>

    <!-- ==================== Toggles + actions ==================== -->
    <div class="card-actions" @click.stop>
      <div
        class="action-toggle"
        :class="{ 'is-updating': loginUpdating }"
        title="Login state — toggle to log this bank in/out"
      >
        <span class="toggle-caption">Login</span>
        <base-toggle
          :key="`login-${loginToggleVersion}`"
          class="mb-0"
          :is-checked="user.isLoggedIn"
          @rsd-checkbox-changed="changeUserLoginStatus"
        ></base-toggle>
        <span v-if="loginUpdating" class="mini-spinner"></span>
      </div>

      <div
        v-if="can('bank.status.toggle')"
        class="action-toggle"
        :class="{
          'is-updating': statusUpdating,
          'is-locked': statusToggleLocked,
        }"
        :title="statusToggleLocked ? 'Blacklisted — contact support to lift.' : 'Bank status'"
      >
        <span class="toggle-caption">Status</span>
        <base-toggle
          :key="`status-${statusToggleVersion}`"
          class="mb-0"
          :is-checked="user.status"
          @rsd-checkbox-changed="changeUserStatus"
        ></base-toggle>
        <span v-if="statusUpdating" class="mini-spinner"></span>
      </div>

      <div
        v-if="viewerIsVendor && can('bank.blacklist')"
        class="action-toggle action-toggle-danger"
        :class="{ 'is-updating': blacklistUpdating }"
        :title="isBlacklisted
          ? 'Currently blacklisted. Toggle off to allow the branch to reactivate.'
          : 'Blacklist this bank — status is forced Inactive and locked until you lift it.'"
      >
        <span class="toggle-caption">Blacklist</span>
        <base-toggle
          :key="`blacklist-${blacklistToggleVersion}`"
          class="mb-0"
          :is-checked="isBlacklisted"
          @rsd-checkbox-changed="changeBlacklist"
        ></base-toggle>
        <span v-if="blacklistUpdating" class="mini-spinner"></span>
      </div>

      <div class="action-spacer"></div>

      <button
        v-if="can('payin.statement.upload')"
        type="button"
        class="action-btn action-upload"
        @click.stop="uploadStatement"
        title="Upload bank statement (CSV)"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Upload
      </button>
      <button
        type="button"
        class="action-btn action-report"
        @click.stop="viewUserStatistics"
        title="View bank report"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 3v18h18" stroke-linecap="round" />
          <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Report
      </button>
      <button
        v-if="can('bank.edit')"
        type="button"
        class="action-btn action-edit"
        @click.stop="editUser"
        title="Edit bank"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 20h9" stroke-linecap="round" />
          <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Edit
      </button>
      <button
        v-if="can('bank.delete')"
        type="button"
        class="action-btn action-delete"
        @click.stop="deleteUser"
        title="Delete bank"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </article>
</template>

<script>
export default {
  name: "user-item",
  emits: ["view-user-statistics", "upload-statement", "edit-user", "delete-user"],
  props: {
    user: { type: Object, required: true },
    statsOverride: { type: Object, default: null },
  },
  data() {
    return {
      loginToggleVersion: 0,
      statusToggleVersion: 0,
      // Bumped when the blacklist call fails so the toggle visually snaps
      // back to its server-truth state (same trick as statusToggleVersion).
      blacklistToggleVersion: 0,
      loginUpdating: false,
      statusUpdating: false,
      blacklistUpdating: false,
    };
  },
  computed: {
    computedRemainingBalance() {
      if (this.statsOverride && this.statsOverride.remainingBalance !== undefined) {
        return this.statsOverride.remainingBalance;
      }
      const balance = parseInt(this.user.balance) || 0;
      const maxBalance = parseInt(this.user.maxBalance) || 0;
      return Math.max(0, maxBalance - balance);
    },
    // Format the branch label as "<Branch Name>(<Branch ID>)". Falls back
    // to the branch's username when name is missing, then to a bare id.
    branchLabel() {
      const name = this.user.branchName || this.user.branchUsername || "";
      const id = this.user.branch_id;
      if (name && id != null) return `${name} (${id})`;
      if (name) return name;
      if (id != null) return String(id);
      return "";
    },
    // The signed-in user's role. We hide branch info from subadmin viewers
    // because every bank in their list trivially belongs to their own branch.
    viewerIsSubAdmin() {
      return this.$store.getters.role === "subadmin";
    },
    // Vendor-level viewer (admin / superadmin). Drives whether the
    // Blacklist toggle is rendered and whether the Status toggle remains
    // interactive when the bank is blacklisted.
    viewerIsVendor() {
      const role = this.$store.getters.role;
      return role === "admin" || role === "superadmin";
    },
    // TINYINT(1) → 0/1 from the API; coerce to bool for template usage.
    isBlacklisted() {
      return Number(this.user.blacklisted_by_vendor || 0) === 1;
    },
    // The Status toggle is locked when the bank is blacklisted AND the
    // viewer isn't the vendor. Vendors can still flip status directly.
    statusToggleLocked() {
      return this.isBlacklisted && !this.viewerIsVendor;
    },
    // Vendor may leave per_day_payin_limit NULL (bank on lifetime-quota
    // semantics). Render "Not set" instead of "₹0" for that case.
    hasDailyLimit() {
      const v = this.user.per_day_payin_limit;
      return v != null && v !== "" && !Number.isNaN(parseFloat(v));
    },
  },
  methods: {
    // Permission gate (main users pass; supporting users gated).
    can(perm) {
      return this.$store.getters.hasPermission(perm);
    },
    initialsOf(u) {
      const s = (u.username || "?").trim();
      const parts = s.split(/[.\s_-]+/).filter(Boolean);
      const a = parts[0]?.[0] || "?";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase().slice(0, 2);
    },
    uploadStatement() {
      this.$emit("upload-statement", this.user.username);
    },
    viewUserStatistics() {
      this.$emit("view-user-statistics", this.user.username);
    },
    editUser() {
      // Card-click + Edit button both route here; no-op without bank.edit so a
      // supporting user without the perm can't open the edit form.
      if (!this.can("bank.edit")) return;
      this.$emit("edit-user", this.user);
    },
    deleteUser() {
      this.$emit("delete-user", this.user);
    },
    fmt(val) {
      if (val === undefined || val === null) return 0;
      return parseInt(val);
    },
    fmtMoney(val) {
      const n = parseInt(val) || 0;
      return "₹" + n.toLocaleString("en-IN");
    },
    async changeUserLoginStatus(status) {
      if (this.loginUpdating) return;
      const username = this.user.username;
      this.loginUpdating = true;

      try {
        await this.$store.dispatch("users/changeUserLoginStatus", { username: username, isLoggedIn: status });

        const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
        });

        Toast.fire({
          icon: "success",
          title: status
            ? "User logged in successfully"
            : "User logged out successfully",
        });
      } catch (error) {
        this.loginToggleVersion++;
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        }).fire({
          icon: "error",
          title: error.message || "Failed to change login status",
        });
      } finally {
        this.loginUpdating = false;
      }
    },
    async changeBlacklist(value) {
      if (this.blacklistUpdating) return;
      this.blacklistUpdating = true;
      const desired = value ? 1 : 0;
      try {
        await this.$store.dispatch("users/changeUserBlacklist", {
          username: this.user.username,
          blacklisted_by_vendor: desired,
        });
        const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: "success",
          title:
            desired === 1
              ? "Bank blacklisted. Status set to Inactive."
              : "Blacklist lifted. Branch can now reactivate the bank.",
        });
      } catch (error) {
        // Snap the toggle back to the server-truth value on failure.
        this.blacklistToggleVersion++;
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
          })
          .fire({
            icon: "error",
            title: error.message || "Failed to update blacklist",
          });
      } finally {
        this.blacklistUpdating = false;
      }
    },
    async changeUserStatus(status) {
      if (this.statusUpdating) return;
      this.statusUpdating = true;
      const data = {
        username: this.user.username,
        status: status,
      };

      try {
        await this.$store.dispatch("users/changeUserStatus", data);

        const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", this.$swal.stopTimer);
            toast.addEventListener("mouseleave", this.$swal.resumeTimer);
          },
        });

        Toast.fire({
          icon: "success",
          title: status
            ? "User activated successfully"
            : "User inactivated successfully",
        });
      } catch (error) {
        this.statusToggleVersion++;
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        }).fire({
          icon: "error",
          title: error.message || "Failed to change status",
        });
      } finally {
        this.statusUpdating = false;
      }
    },
    // Resolve a stat value: check statsOverride first, then user field, then fallback.
    getVal(overrideKey, userKey, fallback = 0) {
      if (this.statsOverride && this.statsOverride[overrideKey] !== undefined) {
        return this.statsOverride[overrideKey];
      }
      if (userKey && this.user[userKey] !== undefined) {
        return this.user[userKey];
      }
      return fallback;
    },
  },
};
</script>

<style scoped>
/* ==================== Card ==================== */
.bank-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.bank-card:hover {
  border-color: #d9d3c1;
  box-shadow: 0 8px 22px -10px rgba(27, 42, 71, 0.18);
  transform: translateY(-1px);
}
.bank-card.is-blacklisted {
  border-color: #fecaca;
  background: linear-gradient(180deg, #fff 0%, #fefafa 100%);
}
.bank-card.is-blacklisted:hover {
  border-color: #fca5a5;
  box-shadow: 0 8px 22px -10px rgba(220, 38, 38, 0.18);
}

/* ==================== Top ==================== */
.card-top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.avatar {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1B2A47, #2C3E63);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.bank-meta { min-width: 0; }
.bank-name {
  font-size: 15px;
  font-weight: 700;
  color: #1b2a47;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  letter-spacing: -0.005em;
}
.bank-sub {
  font-size: 12px;
  color: #64748b;
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 2px;
}
.upi-tag {
  font-family: "SFMono-Regular", Menlo, monospace;
  font-size: 11.5px;
  color: #475569;
  background: #f4f0e5;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #ede9df;
}
.branch-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #475569;
  font-weight: 500;
}
.branch-tag svg { width: 12px; height: 12px; opacity: 0.7; }
.branch-none { color: #94a3b8; font-style: italic; font-size: 11.5px; }
.dot-sep { color: #cbd5e1; }

/* ==================== Badges ==================== */
.card-badges {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.pill-success    { background: #d1fae5; color: #065f46; }
.pill-neutral    { background: #f1f5f9; color: #64748b; }
.pill-info       { background: #eef2f7; color: #1B2A47; }
.pill-blacklist  { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

/* ==================== Stats ==================== */
.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #f4f0e5;
}
.stat-cell {
  background: #fbfaf7;
  border: 1px solid #f1ede2;
  border-radius: 10px;
  padding: 8px 10px;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #1b2a47;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}
.stat-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
.stat-empty {
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
  font-size: 13px;
}
@media (max-width: 720px) {
  .card-stats { grid-template-columns: 1fr 1fr; }
  .card-stats > .stat-cell:nth-child(3) { grid-column: 1 / -1; }
}

/* ==================== Actions ==================== */
.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f4f0e5;
  flex-wrap: wrap;
}
.action-spacer { flex: 1; }

.action-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #fbfaf7;
  border: 1px solid #f1ede2;
  transition: opacity 0.2s;
}
.action-toggle.is-updating { opacity: 0.6; pointer-events: none; }
.action-toggle.is-locked   { opacity: 0.55; pointer-events: none; }
.action-toggle-danger      { background: #fef2f2; border-color: #fecaca; }

.toggle-caption {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.action-toggle-danger .toggle-caption { color: #991b1b; }

.mini-spinner {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
.action-toggle-danger .mini-spinner { border-top-color: #dc2626; }
@keyframes spin { to { transform: rotate(360deg); } }

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
  font-family: inherit;
}
.action-btn svg { width: 14px; height: 14px; }
.action-btn:hover { border-color: #cbd5e1; background: #f8fafc; }

.action-upload:hover { color: #047857; background: #ecfdf5; border-color: #a7f3d0; }
.action-report:hover { color: #1B2A47; background: #eef2f7; border-color: #cbd5e1; }
.action-edit:hover   { color: #1B2A47; background: #eef2f7; border-color: #cbd5e1; }
.action-delete       { color: #b91c1c; }
.action-delete:hover { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
</style>
